"use client";

import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { subscribeShared } from "@/lib/firestore-listener-registry";
import { type Chat, ChatStatus } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

/**
 * Active match chats the user is a participant of.
 *
 * Used by the unified chat dropdown to surface match chats that exist
 * (created at challenge time by `createChatForMatch`) but have no
 * `chatInboxes` entry yet because no one has posted. Without this, fresh
 * match chats are invisible until the first message.
 *
 * Pass `enabled: false` to keep this listener torn down (e.g. when the
 * dropdown is closed) — the inbox alone covers chats that have any
 * activity, so the always-on cost is only justified while the user is
 * actually looking at the chat list.
 */
export function useUserMatchChats(
  userId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(() => ["userMatchChats", userId] as const, [userId]);

  const pendingRef = useRef<{
    resolve: (chats: Chat[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !isVisible || !userId) return;

    const unsubscribe = subscribeShared<Chat[]>(
      `userMatchChats:${userId}`,
      (onData, onError) => {
        const chatsQuery = query(
          collection(db, "chats"),
          where("participantIds", "array-contains", userId),
          where("status", "==", ChatStatus.ACTIVE),
        );
        return onSnapshot(
          chatsQuery,
          (snapshot) => {
            const next = snapshot.docs.map((doc) =>
              formatFirestoreDocument(doc),
            ) as Chat[];
            onData(next);
          },
          onError,
        );
      },
      {
        onData: (next) => {
          if (pendingRef.current) {
            pendingRef.current.resolve(next);
            pendingRef.current = null;
          }
          queryClient.setQueryData(queryKey, next);
        },
        onError: (error) => {
          console.error("Error fetching user match chats:", error);
          if (pendingRef.current) {
            pendingRef.current.reject(error);
            pendingRef.current = null;
          }
        },
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached = queryClient.getQueryData<Chat[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [userId, queryClient, queryKey, isVisible, enabled]);

  return useQuery({
    queryKey,
    queryFn: () =>
      new Promise<Chat[]>((resolve, reject) => {
        if (!userId) {
          resolve([]);
          return;
        }
        const cached = queryClient.getQueryData<Chat[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (next) => {
            clearTimeout(timeoutId);
            resolve(next);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      }),
    enabled: !!userId && enabled,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });
}
